import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  ThumbUp,
  ThumbDown,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Leave, LeaveStatus, LeaveType } from '../types';
import Layout from '../components/Layout/Layout';

const LeaveApprovals: React.FC = () => {
  const navigate = useNavigate();
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [rejectionDialog, setRejectionDialog] = useState(false);
  const [approvalType, setApprovalType] = useState<'approve' | 'reject'>('approve');
  const [comments, setComments] = useState('');
  const [reason, setReason] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPendingLeaves();
  }, []);

  const loadPendingLeaves = async () => {
    try {
      setLoading(true);
      const data = await apiService.getPendingLeaves();
      setLeaves(data);
    } catch (error) {
      console.error('Failed to load pending leaves:', error);
      setError('Failed to load pending leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async () => {
    if (!selectedLeave) return;

    try {
      if (approvalType === 'approve') {
        await apiService.approveLeave(selectedLeave.id);
        setSuccess('Leave request approved successfully!');
      } else {
        await apiService.rejectLeave(selectedLeave.id, reason);
        setSuccess('Leave request rejected successfully!');
      }

      // Clear form
      setComments('');
      setReason('');
      setApprovalDialog(false);
      setRejectionDialog(false);
      setSelectedLeave(null);

      // Reload leaves
      loadPendingLeaves();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Failed to process leave request:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error?.message || 
                          error.message || 
                          'Failed to process leave request';
      setError(errorMessage);
    }
  };

  const openApprovalDialog = (leave: Leave) => {
    setSelectedLeave(leave);
    setApprovalType('approve');
    setComments('');
    setApprovalDialog(true);
  };

  const openRejectionDialog = (leave: Leave) => {
    setSelectedLeave(leave);
    setApprovalType('reject');
    setComments('');
    setReason('');
    setRejectionDialog(true);
  };

  const getLeaveTypeLabel = (type: LeaveType) => {
    switch (type) {
      case LeaveType.FULL_DAY:
        return 'Full Day';
      case LeaveType.HALF_DAY:
        return 'Half Day';
      case LeaveType.HOURS:
        return 'Hours';
      default:
        return type;
    }
  };

  const getStatusColor = (status: LeaveStatus) => {
    switch (status) {
      case LeaveStatus.PENDING:
        return 'warning';
      case LeaveStatus.APPROVED:
        return 'success';
      case LeaveStatus.REJECTED:
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Leave Approvals
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and approve submitted leave requests
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {leaves.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              No pending leave requests for approval
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary">
              All submitted leave requests have been processed
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaves.map((leave) => (
                <TableRow key={leave.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {leave.user?.firstName} {leave.user?.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {leave.user?.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getLeaveTypeLabel(leave.type)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(leave.startDate).toLocaleDateString()} -{' '}
                    {new Date(leave.endDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {leave.type === LeaveType.HOURS ? `${leave.hours} hours` : '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                      {leave.reason}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={leave.status}
                      color={getStatusColor(leave.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {leave.createdAt
                      ? new Date(leave.createdAt).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/leaves/${leave.id}`)}
                        title="View Details"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => openApprovalDialog(leave)}
                        title="Approve"
                      >
                        <ThumbUp />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openRejectionDialog(leave)}
                        title="Reject"
                      >
                        <ThumbDown />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Leave Request</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Are you sure you want to approve this leave request?
              </Typography>
              {selectedLeave && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>Employee:</strong> {selectedLeave.user?.firstName} {selectedLeave.user?.lastName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Type:</strong> {getLeaveTypeLabel(selectedLeave.type)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Dates:</strong> {new Date(selectedLeave.startDate).toLocaleDateString()} - {new Date(selectedLeave.endDate).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Reason:</strong> {selectedLeave.reason}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional comments (optional)"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any additional comments..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
          <Button
            onClick={handleApproval}
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectionDialog} onClose={() => setRejectionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Leave Request</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Please provide a reason for rejecting this leave request.
              </Typography>
              {selectedLeave && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>Employee:</strong> {selectedLeave.user?.firstName} {selectedLeave.user?.lastName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Type:</strong> {getLeaveTypeLabel(selectedLeave.type)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Dates:</strong> {new Date(selectedLeave.startDate).toLocaleDateString()} - {new Date(selectedLeave.endDate).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Reason:</strong> {selectedLeave.reason}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Rejection reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                error={!reason.trim()}
                helperText={!reason.trim() ? 'Rejection reason is required' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional comments (optional)"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any additional comments..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialog(false)}>Cancel</Button>
          <Button
            onClick={handleApproval}
            variant="contained"
            color="error"
            startIcon={<Cancel />}
            disabled={!reason.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default LeaveApprovals;
